import {
  ClassificationNode,
  ClassificationNodesPagination,
} from "../models/classifications";
import { ProjectId } from "../types";
import { AssetsHttpClient } from "./createAssetsHttpClient";
import { buildSearchString } from "./helpers";

/** acc-classifications' own client uses this page size against the same endpoint. */
const NODES_PAGE_LIMIT = 10000;

type ClassificationNodesResponse = {
  pagination: ClassificationNodesPagination;
  results: ClassificationNode[];
};

/**
 * Creates an acc-classifications client from an `AssetsHttpClient` pointed at the Classifications
 * backend origin — a **different origin** than the Assets service, so it needs its own client
 * instance (the "second-origin" pattern shared with `locationClient`/`hqClient`).
 *
 * Read-only: acc-classifications owns tree authoring; Assets only resolves nodes.
 *
 * @example
 * ```ts
 * const classificationsHttp = createAssetsHttpClient({
 *   fetch: (url, init) => fetchWithToken(url, { ...init, headers: { region: getRegion(), ...init?.headers } }),
 *   baseUrl: getEnvValue("CLASSIFICATIONS_BACKEND"),
 * });
 * const client = getClassificationNodeClient(classificationsHttp, projectId);
 * const nodes = await client.listAllTipNodes(treeId);
 * ```
 */
export const getClassificationNodeClient = (
  http: AssetsHttpClient,
  projectId: ProjectId
) => {
  const getTipNodes = (
    treeId: string,
    { limit, offset }: { limit?: number; offset?: number } = {}
  ): Promise<ClassificationNodesResponse> => {
    const searchString = buildSearchString({ limit, offset });
    return http.get<ClassificationNodesResponse>(
      `${http.baseUrl}/v1/projects/${projectId}/trees/${treeId}/versions/tip/nodes${
        searchString ? `?${searchString}` : ""
      }`
    );
  };

  /**
   * Every node of a tree's tip version. Paginated by `offset`/`limit` with a `totalResults` count —
   * there is no `nextUrl` on this endpoint, unlike the Locations equivalent.
   */
  const listAllTipNodes = async (
    treeId: string
  ): Promise<ClassificationNode[]> => {
    const nodes: ClassificationNode[] = [];

    for (;;) {
      const response = await getTipNodes(treeId, {
        limit: NODES_PAGE_LIMIT,
        offset: nodes.length,
      });
      nodes.push(...response.results);

      if (
        !response.results.length ||
        nodes.length >= response.pagination.totalResults
      ) {
        return nodes;
      }
    }
  };

  return { getTipNodes, listAllTipNodes };
};

/** The shape returned by {@link getClassificationNodeClient}. */
export type ClassificationNodeClient = ReturnType<
  typeof getClassificationNodeClient
>;
